/**
 * @file NumberPresets.tsx
 * @description Ступени-подсказки под `NumberSliderField` (phase19.16, партия
 * SB120 «свобода выбора», этап 0; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §2): кнопки
 * «[0] [XS 8] [S 16] [M 32] [L 64]» — не ограничения, а быстрый выбор.
 * Совпавшая со значением обведена (`aria-pressed`); пока значение
 * наследуемое, ступень наследуемого обведена пунктиром.
 * Не больше 5 штук.
 * Высота 48 на касании, 32 с мышью (`can-hover`); ряд переносится на 360.
 */

import { cn } from "@/lib/utils";

export interface NumberPreset {
  label: string;
  /** Хранимое значение (в тех же единицах, что `value` поля). */
  value: number;
}

interface Props {
  presets: readonly NumberPreset[];
  /** Текущее хранимое значение; undefined — наследуемое (ни одна не обведена). */
  value: number | undefined;
  /** Наследуемое значение — его ступень обводится пунктиром, пока своего нет. */
  inheritValue?: number;
  /** Подпись наследуемого для скринридера: «20 % — по умолчанию». */
  inheritLabel?: string;
  /** Шаг поля: значение считается ступенью, если ближе половины шага. */
  tolerance: number;
  /** Подпись группы для скринридера: «Отступ сверху: ступени». */
  label: string;
  disabled?: boolean;
  onPick: (value: number) => void;
}

export function NumberPresets({ presets, value, inheritValue, inheritLabel, tolerance, label, disabled, onPick }: Props) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1">
      {presets.map((preset) => {
        // Половина шага: `< step` из-за дробной арифметики ловил соседний шаг
        // (0,25 − 0,2 = 0,04999… < 0,05), половина — только своё значение.
        const near = (v: number | undefined) => v !== undefined && Math.abs(v - preset.value) < tolerance / 2;
        const active = near(value);
        const asInherited = value === undefined && near(inheritValue);
        return (
          <button
            key={preset.label}
            type="button"
            aria-pressed={active}
            aria-label={asInherited && inheritLabel ? `${preset.label} — ${inheritLabel}` : undefined}
            disabled={disabled}
            onClick={() => onPick(preset.value)}
            className={cn(
              "inline-flex h-12 min-w-12 items-center justify-center rounded-lg px-3 text-sm font-medium outline-none can-hover:h-8 can-hover:min-w-8 can-hover:px-2",
              "focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              active && "bg-surface-lowest text-on-surface [box-shadow:inset_0_0_0_2px_var(--primary)]",
              asInherited && "border border-dashed border-primary bg-surface-low text-on-surface",
              !active && !asInherited && "bg-surface-low text-on-surface-variant hover:text-on-surface",
            )}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}
